import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { Mail, Sparkles } from "@/lib/icons";
import BrandLogo from "@/components/BrandLogo";
import { Input } from "@/components/ui/input";
import PasswordField from "@/components/PasswordField";
import { useSeo } from "@/lib/seo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useSeo({ title: "Admin Sign In | PRONET", noindex: true });

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(form.email, form.password); nav("/admin", { replace: true }); }
    catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[600px] h-[600px] -top-40 -left-20 opacity-50" />
        <div className="blob blob-magenta w-[500px] h-[500px] bottom-0 right-0 opacity-40" style={{ animationDelay: "-10s" }} />
      </div>

      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Aperture"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="admin-login-overlay absolute inset-0 bg-gradient-to-br from-ink-950/40 via-violet-950/50 to-magenta-700/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="overline text-fuchsia-300 mb-4">PRONET Admin</div>
          <div className="font-display text-5xl leading-tight text-white max-w-md">
            Secure control panel for the <span className="italic text-gradient">PRONET</span> professional network.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 relative">
        <form onSubmit={submit} className="w-full max-w-md glass rounded-3xl p-8 md:p-10 fade-up-slow">
          <div className="flex items-center gap-2 mb-8">
            <BrandLogo size={36} testid="admin-login-brand" />
            <div>
              <div className="font-display text-xl text-white">PRONET</div>
              <div className="overline text-[9px] text-fuchsia-300/80 -mt-0.5">Admin</div>
            </div>
          </div>
          <h1 className="font-display text-4xl text-white">Welcome back</h1>
          <p className="text-sm text-white/50 mt-1">Sign in to manage the PRONET database.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="overline text-white/60 text-[10px]">Email</label>
              <div className="relative mt-2">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input type="email" required autoComplete="email" data-testid="admin-email"
                  className="pl-9 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="overline text-white/60 text-[10px]">Password</label>
              <div className="mt-2">
                <PasswordField testid="admin-password" autoComplete="current-password"
                  className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
              </div>
            </div>
          </div>

          {err && (
            <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">
              {err}
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Sparkles size={14} /> {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-6 text-xs leading-relaxed text-white/40">
            Authorized administrators only. Credentials are managed securely through the server configuration.
          </div>
        </form>
      </div>
    </div>
  );
}
